<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue';
import { format, intervalToDuration } from 'date-fns';
import WaveSurfer from 'wavesurfer.js';
import Recorder from 'opus-recorder/dist/recorder.min.js';
import encoderPath from 'opus-recorder/dist/encoderWorker.min.js?url';

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
]);

const OGG_OPUS_MIME_TYPE = 'audio/ogg;codecs=opus';

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const recorder = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
const recordingStartedAt = ref(null);
const recordingProgressInterval = ref(null);
const recordedAudioUrl = ref('');

const getFileExtension = () => 'ogg';

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const supportsOpusRecording = () =>
  typeof Recorder?.isRecordingSupported === 'function' &&
  Recorder.isRecordingSupported();

const clearProgressTimer = () => {
  if (recordingProgressInterval.value) {
    clearInterval(recordingProgressInterval.value);
    recordingProgressInterval.value = null;
  }
};

const emitRecordingProgress = () => {
  if (!recordingStartedAt.value) {
    emit('recorderProgressChanged', '00:00');
    return;
  }

  emit(
    'recorderProgressChanged',
    formatTimeProgress(Date.now() - recordingStartedAt.value)
  );
};

const startProgressTimer = () => {
  clearProgressTimer();
  emitRecordingProgress();
  recordingProgressInterval.value = setInterval(emitRecordingProgress, 250);
};

const revokeRecordedAudioUrl = () => {
  if (recordedAudioUrl.value) {
    URL.revokeObjectURL(recordedAudioUrl.value);
    recordedAudioUrl.value = '';
  }
};

const handleRecordedAudio = arrayBuffer => {
  revokeRecordedAudioUrl();

  const blob = new Blob([arrayBuffer], {
    type: OGG_OPUS_MIME_TYPE,
  });
  const audioUrl = URL.createObjectURL(blob);
  const file = new File([blob], `${getUuid()}.${getFileExtension()}`, {
    type: OGG_OPUS_MIME_TYPE,
  });

  recordedAudioUrl.value = audioUrl;
  wavesurfer.value.load(audioUrl);
  emit('finishRecord', {
    file,
    name: file.name,
    previewUrl: audioUrl,
    size: file.size,
    type: file.type,
  });
  hasRecording.value = true;
  isRecording.value = false;
};

const createRecorder = () => {
  if (!supportsOpusRecording()) {
    throw new Error('Opus recording is not supported in this browser');
  }

  const nextRecorder = new Recorder({
    encoderPath,
    encoderApplication: 2048,
    encoderSampleRate: 48000,
    numberOfChannels: 1,
    streamPages: false,
  });

  nextRecorder.ondataavailable = handleRecordedAudio;
  nextRecorder.onstop = () => {
    isRecording.value = false;
    recorder.value = null;
  };

  return nextRecorder;
};

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#1F93FF',
    progressColor: '#6E6F73',
    height: 100,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });
};

const stopRecording = async () => {
  if (isRecording.value && recorder.value) {
    clearProgressTimer();
    recordingStartedAt.value = null;
    await recorder.value.stop();
  }
};

const startRecording = async () => {
  try {
    revokeRecordedAudioUrl();
    hasRecording.value = false;
    isPlaying.value = false;
    recorder.value = createRecorder();
    recordingStartedAt.value = Date.now();
    startProgressTimer();
    await recorder.value.start();
    isRecording.value = true;
  } catch {
    clearProgressTimer();
    recordingStartedAt.value = null;
    recorder.value = null;
    isRecording.value = false;
    emit('finishRecord', null);
  }
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  clearProgressTimer();

  if (recorder.value) {
    recorder.value.close();
  }

  revokeRecordedAudioUrl();

  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
});

defineExpose({ playPause, stopRecording, recorder });
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
